import { Client } from '@stomp/stompjs';
import { CODE_ENV_LIST, LOGIN_TOKEN_NAME, SSE_TYPE } from '@/common/constants/ConstCode';
import debounce from 'lodash/debounce';
import store from '@/store';
import { isLocalChatUiFixtureEnabled } from '@/views/chat/__fixtures__/chatUiFixture';

const sseUrl = (userNo) => `/eventStream/${userNo}`;

let sseClient; // sse client
let subscription; // sse subscription

const waitUntilConnected = async () => {
  while (!sseClient?.connected) {
    await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms 대기
  }
  return true;
};

const actions = {
  emitterInitialize: async ({ commit, state, getters }, { userNo, accessToken }) => {
    store.commit('chat/setHeader', { userNo, accessToken });
    // LOCAL UI FIXTURE: never construct or activate the event-stream STOMP client.
    if (isLocalChatUiFixtureEnabled()) return true;
    // if ((sseClient && (sseClient?.active ?? false)) || sseClient?.connected) return;
    if (sseClient?.active || sseClient?.connected) return;

    sseClient = new Client({
      brokerURL: CODE_ENV_LIST.SSE_SERVER_URL,
      connectHeaders: {
        userNo: userNo,
        accessToken: accessToken,
        tokenType: LOGIN_TOKEN_NAME,
        channel: 'EVENT_STREAM'
      },
      debug: function (str) {
        // if (isDev) console.warn('SSE debug : ' + str);
      },
      reconnectDelay: 500,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        console.log('Connected to SSE STOMP server');
        actions.emitterSubscribe({ state, commit, getters });
      },
      onDisconnect: () => {
        console.log('Disconnected from SSE STOMP server');
      },
      onStompError: (frame) => {
        console.warn('SSE Broker reported error: ' + frame.headers['message']);
        console.warn('SSE Additional details: ' + frame.body);
      }
    });

    sseClient.activate();

    return await waitUntilConnected();
  },

  /**
   * sse 객체 연결
   */
  emitterConnect: async () => {
    if (isLocalChatUiFixtureEnabled()) return true;

    if (!sseClient) {
      console.error('SSE STOMP is NOT INITIALIZED');
      return;
    }

    try {
      sseClient.activate();
    } catch (e) {
      console.error('EMITTER CONNECT ERROR', e);
    }
  },

  emitterDisconnect: ({ state, commit }) => {
    if (isLocalChatUiFixtureEnabled()) return;

    const { userNo, accessToken } = store.getters['chat/getHeader'];
    // console.debug(FONT_GREEN + 'EMITTER DISCONNECT' + RESET);

    if (subscription) {
      subscription.unsubscribe({
        userNo: userNo + '',
        accessToken: accessToken,
        channel: 'EVENT_STREAM'
      });
      subscription = null;
    }
    if (sseClient) sseClient.deactivate();
    sseClient = null;
  },

  emitterReconnect: async ({ state, commit, getters }) => {
    if (isLocalChatUiFixtureEnabled()) return true;

    const header = store.getters['chat/getHeader'];
    header.channel = 'EVENT_STREAM';

    if (sseClient) {
      sseClient.activate();
      return true;
    } else {
      return await actions.emitterInitialize({ commit, state, getters }, header);
      // await actions.emitterConnect();
    }
    // await actions.emitterSubscribe({ state, commit, getters });
  },

  emitterSubscribe: debounce(async ({ state, commit, getters }) => {
    if (isLocalChatUiFixtureEnabled()) return true;

    if (!sseClient?.connected) {
      return await actions.emitterReconnect({ state, commit, getters });
    }

    const onMessage = async (message) => {
      const path = window.location.pathname;

      // 서버에서 발행한 데이터
      const container = JSON.parse(message.body);

      const { chat_status, order_status, store_disabled, new_chat, leave_chat } = SSE_TYPE;

      console.warn('Emitter onMessage - eventType ::: ', container.eventType);

      if (container.eventType === chat_status) {
        if (container?.badge) {
          store.commit('chat/setBubbleCounts', container?.badge);
        }
        if (container?.latest) {
          const { roomNo, count, latest } = container?.latest;
          store.commit('chat/setRoomUnreadBubbleCount', {
            roomNo: roomNo,
            count: count
          });
          store.commit('chat/updateLastBubble', {
            latestBubble: latest,
            roomNo: roomNo
          });
        }
      }
      if (container.eventType === order_status) {
        orderState(path, container?.order?.orderNo)(container.order);
      }
      if (container.eventType === store_disabled) {
        storeChat()(container.disabled);
      }
      if (container.eventType === new_chat) {
        store.commit('chat/appendRoom', container?.newChat?.room);
      }
      if (container.eventType === leave_chat) {
        exitRoom()(container?.leaveChat ?? 0);
      }
    };

    const { userNo, accessToken } = store.getters['chat/getHeader'];

    subscription = sseClient.subscribe(sseUrl(userNo), onMessage, {
      userNo: userNo + '',
      accessToken: accessToken,
      channel: 'EVENT_STREAM'
    });
  }, 300),

  emitterUnSubscribe: debounce(() => {
    if (isLocalChatUiFixtureEnabled()) return;

    const { userNo, accessToken } = store.getters['chat/getHeader'];
    if (!sseClient?.connected) return;
    if (subscription) {
      subscription.unsubscribe({
        userNo: userNo + '',
        accessToken: accessToken,
        channel: 'EVENT_STREAM'
      });
      subscription = null;
    }
  }, 300),

  emitterWake: debounce(async ({ state, commit, getters }) => {
    if (isLocalChatUiFixtureEnabled()) return true;

    await actions.emitterReconnect({ state, commit, getters }).then((result) => {
      console.log('Emitter Wake Up Result :: ', result);
    });
  }, 300),

  emitterHibernate: debounce(async () => {
    if (isLocalChatUiFixtureEnabled()) return;

    const { userNo, accessToken } = store.getters['chat/getHeader'];
    if (!sseClient?.connected) return;

    console.warn('SSE unsubscribe');
    if (subscription) {
      subscription.unsubscribe({
        userNo: userNo + '',
        accessToken: accessToken,
        channel: 'EVENT_STREAM'
      });
      subscription = null;
    }

    if (sseClient) {
      await sseClient.deactivate();
      sseClient = null;
    }
  }, 300)
};

const orderState = (path, orderNo) => {
  const lobbyWork = (parsedData) => {
    store.commit('chat/setLobbyOrderState', parsedData);
  };
  const roomWork = (parsedData) => {
    store.commit('chat/setRoomOrderState', parsedData);
  };

  const selectedRoom = store.getters['chat/getSelectedRoom'];
  const isCurrentOrderRoom =
    path.startsWith('/room/') &&
    selectedRoom?.roomType === 'ORDER' &&
    Number(selectedRoom?.orderNo ?? 0) === Number(orderNo ?? 0);

  return isCurrentOrderRoom ? roomWork : lobbyWork;
};

const storeChat = () => {
  return ({ storeNo, isRoomChattable }) => {
    store.commit('chat/setRoomStoreChatState', {
      storeNo,
      isRoomChattable
    });
  };
};

const exitRoom = () => {
  console.log('EXIT ROOM');
  // const removeRoom = ({ roomNo }) => commit('leaveRoomAtLobby', roomNo);
  return ({ roomNo }) => {
    store.commit('chat/leaveRoomAtRoom', roomNo);
  };
};

export default {
  namespaced: true,
  actions
};
